/**
 * Self-heal for stale deployments.
 *
 * SkyDeck code-splits heavily — every pane view, dialog and settings screen
 * is a lazy chunk. After a deploy the asset hashes change, so a client still
 * running the previous app shell (or sitting behind a CDN/service worker that
 * hasn't caught up) can request a chunk URL that no longer exists. SPA hosts
 * answer those with `index.html` (a `text/html` response); the dynamic import
 * then rejects with "Failed to fetch dynamically imported module", the lazy
 * component renders as `undefined`, and the deck crashes.
 *
 * We can't prevent the hash mismatch, but we can recover from it: when a chunk
 * fails to load, reload once to pull the fresh `index.html` + chunks. A short
 * time-based guard prevents a reload loop if the asset is genuinely gone.
 */

const RELOAD_KEY = 'skydeck:chunk-reload-at';
const RELOAD_COOLDOWN = 10_000;

/**
 * Whether an error/rejection looks like a failed dynamic import of a lazy chunk
 * (as opposed to an ordinary runtime error inside a chunk that did load).
 */
export const isChunkLoadError = (reason: unknown): boolean => {
	let message = '';

	if (typeof reason === 'string') {
		message = reason;
	} else if (reason instanceof Error) {
		message = reason.message;
	} else if (reason != null && typeof (reason as { message?: unknown }).message === 'string') {
		message = (reason as { message: string }).message;
	}

	return (
		/failed to fetch dynamically imported module/i.test(message) ||
		/error loading dynamically imported module/i.test(message) ||
		/importing a module script failed/i.test(message) ||
		/expected a javascript(-or-wasm)? module script/i.test(message)
	);
};

const reloadOnce = (): void => {
	try {
		const last = Number(sessionStorage.getItem(RELOAD_KEY)) || 0;

		// Already reloaded very recently — the asset is probably truly gone, so
		// stop here rather than spinning in an endless refresh loop.
		if (Date.now() - last < RELOAD_COOLDOWN) {
			return;
		}

		sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
	} catch {
		// sessionStorage may be unavailable in some privacy modes — fall through
		// and reload anyway; the worst case is one extra refresh.
	}

	location.reload();
};

/** Install global listeners that reload the page when a lazy chunk fails to load. */
export const installChunkReloadGuard = (): void => {
	// Vite dispatches this from its dynamic-import preload helper on failure.
	window.addEventListener('vite:preloadError', (event) => {
		event.preventDefault();
		reloadOnce();
	});

	// Solid's `lazy()` imports reject straight onto the microtask queue.
	window.addEventListener('unhandledrejection', (event) => {
		if (isChunkLoadError(event.reason)) {
			reloadOnce();
		}
	});
};
